import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X, Check, Activity, Clock, Play, Settings,
    Target, Layers, BarChart2, Database, MapPin,
    Scan, TrendingUp, Search, ArrowRight,
    Globe, Beaker, FileText, Monitor, Cpu,
    Crosshair, Ruler, Calculator, Box,
    ChevronDown, ChevronRight, Filter, BookOpen, Atom,
    Zap, Timer
} from 'lucide-react';
import { useSimulationContext } from '../../context/SimulationContext';
import { PARTICLES } from '../../data/physicsConstants';
import { useSimulation } from '../../hooks/useSimulation';
import presetsData from '../../data/presets.json';

// ============================================
// DATA & UTILS
// ============================================
const FACILITIES = presetsData.facilities;
const CATEGORIES = presetsData.categories;

// Standard monochrome icon set - Complete mapping to prevent emoji fallbacks
const ICON_MAP = {
    // Direct mappings
    Atom, Activity, Clock, Zap, Layers, Target, Settings, Search, Box,
    BarChart2, Scan, TrendingUp, Ruler, Calculator, Beaker, Globe,
    Crosshair, FileText, Monitor, Cpu,

    // Alias mappings
    Lightbulb: Zap,
    Star: Target,
    Scale: Ruler,
    Eye: Scan,
    Heart: Activity,
    Award: Target,
    Wrench: Settings,
    GraduationCap: BookOpen,
    BarChart: BarChart2,
    TrendingDown: TrendingUp,
    CheckCircle: Check,
    Maximize: Target,
    Minimize: Target,
    Magnet: Monitor,
    Rocket: TrendingUp,
    Radio: Activity,
    Cloud: Database,
    Sun: Activity
};

// Apple-style UI Constants
const UI = {
    bg: "bg-[#f5f5f7]",
    card: "bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300",
    textMain: "text-[#1d1d1f]",
    textSec: "text-[#86868b]",
    accent: "bg-[#1d1d1f]",
    accentText: "text-white",
    border: "border-gray-200"
};

// ============================================
// COMPONENTS
// ============================================

const CircularGauge = ({ value, max, label, unit, size = 80, decimals = 1 }) => {
    const percentage = Math.min(100, (value / max) * 100);
    const radius = (size - 6) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
        <div className="flex flex-col items-center">
            <div className="relative" style={{ width: size, height: size }}>
                <svg className="transform -rotate-90" width={size} height={size}>
                    <circle cx={size / 2} cy={size / 2} r={radius} stroke="#e5e7eb" strokeWidth="3" fill="none" />
                    <circle
                        cx={size / 2} cy={size / 2} r={radius} stroke="#1d1d1f" strokeWidth="3" fill="none"
                        strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
                        className="transition-all duration-500 ease-out"
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-sm font-semibold ${UI.textMain} font-mono tracking-tight`}>
                        {typeof value === 'number' ? value.toFixed(decimals) : value}
                    </span>
                    <span className={`text-[9px] ${UI.textSec} font-medium uppercase`}>{unit}</span>
                </div>
            </div>
            <span className={`mt-2 text-[10px] ${UI.textSec} font-semibold uppercase tracking-wider`}>{label}</span>
        </div>
    );
};

const DataRow = ({ label, value, unit = '' }) => (
    <div className={`flex justify-between items-center py-2 border-b ${UI.border} last:border-0`}>
        <span className={`text-xs font-medium ${UI.textSec}`}>{label}</span>
        <span className={`text-sm font-mono font-medium ${UI.textMain}`}>
            {value}{unit && <span className={`text-xs ml-1 font-sans ${UI.textSec}`}>{unit}</span>}
        </span>
    </div>
);

const PresetCard = ({ preset, isActive, onClick, featured = false }) => {
    const IconComponent = ICON_MAP[preset.icon] || FileText;

    // Clean Badges (Wireframe style)
    const diffLabel = preset.difficulty ? preset.difficulty.charAt(0).toUpperCase() + preset.difficulty.slice(1) : 'Beginner';

    return (
        <button
            onClick={onClick}
            className={`
                w-full text-left p-5 rounded-xl border transition-all duration-200 group relative overflow-hidden
                ${isActive
                    ? 'border-gray-400 bg-white ring-1 ring-gray-200 shadow-md'
                    : 'border-transparent bg-white shadow-sm hover:shadow-md hover:scale-[1.01]'
                }
            `}
        >
            <div className="flex items-start gap-4">
                <div className={`
                    w-10 h-10 rounded-lg flex items-center justify-center shrink-0 
                    ${isActive ? 'bg-[#1d1d1f] text-white' : 'bg-[#f5f5f7] text-[#1d1d1f] group-hover:bg-[#e8e8ed]'}
                    transition-colors
                `}>
                    <IconComponent size={20} strokeWidth={1.5} />
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                        <div className={`font-semibold text-[15px] ${UI.textMain} leading-tight`}>
                            {preset.name}
                        </div>
                        {featured && <span className="text-[9px] font-bold text-[#86868b] uppercase tracking-wider border border-gray-200 px-1.5 py-0.5 rounded">Featured</span>}
                    </div>

                    <div className="flex items-center gap-3 mt-3">
                        <span className="text-[10px] px-2 py-0.5 rounded-full border border-gray-200 text-[#86868b] font-medium bg-[#f5f5f7]">
                            {diffLabel}
                        </span>
                        {preset.duration && (
                            <span className={`text-[10px] ${UI.textSec} flex items-center gap-1.5`}>
                                <Clock size={10} /> {preset.duration}
                            </span>
                        )}
                        <span className={`text-[10px] ${UI.textSec} flex items-center gap-1.5 border-l ${UI.border} pl-3`}>
                            {FACILITIES[preset.beamline]?.shortName || 'Facility'}
                        </span>
                    </div>

                    <p className={`text-[12px] ${UI.textSec} mt-2 leading-relaxed line-clamp-2`}>
                        {preset.description}
                    </p>
                </div>
            </div>
        </button>
    );
};

// ============================================
// MAIN MODAL
// ============================================

const ControlRoomModal = ({ isOpen, onClose }) => {
    // FIX: Use useSimulation hook to get access to the actual engine runner
    const { state, actions, runSimulation } = useSimulation(); 
    // const { state, actions } = useSimulationContext(); // <-- OLD: Missing run logic
    const [selectedBeamline, setSelectedBeamline] = useState('cern-t9');
    const [viewMode, setViewMode] = useState('featured');
    const [searchQuery, setSearchQuery] = useState('');

    const currentBeamline = FACILITIES[selectedBeamline];

    // Hooks Logic
    const facilityGroups = useMemo(() => {
        const groups = { 'CERN': [], 'Germany': [], 'Switzerland': [], 'USA': [], 'Japan': [], 'Education': [] };
        Object.entries(FACILITIES).forEach(([id, facility]) => {
            if (id.startsWith('cern')) groups['CERN'].push([id, facility]);
            else if (id.startsWith('desy')) groups['Germany'].push([id, facility]);
            else if (id.startsWith('psi')) groups['Switzerland'].push([id, facility]);
            else if (id.startsWith('fnal')) groups['USA'].push([id, facility]);
            else if (id.startsWith('jparc')) groups['Japan'].push([id, facility]);
            else groups['Education'].push([id, facility]);
        });
        return Object.entries(groups).filter(([_, facilities]) => facilities.length > 0);
    }, []);

    const filteredPresets = useMemo(() => {
        let presets = presetsData.presets;
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            presets = presets.filter(p => p.name.toLowerCase().includes(q));
        }
        if (viewMode === 'facility') presets = presets.filter(p => p.beamline === selectedBeamline);
        return presets;
    }, [viewMode, selectedBeamline, searchQuery]);

    const handleBeamlineChange = (id) => {
        setSelectedBeamline(id);
        setViewMode('facility');
        const bl = FACILITIES[id];
        if (bl) {
            actions.setBeamlineLength(bl.typicalLength);
            if (!bl.particles.includes(state.particle.type)) actions.setParticle(bl.particles[0]);
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                className="fixed inset-0 z-[70] flex items-center justify-center p-4"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            >
                <div className="absolute inset-0 bg-[#d2d2d7]/60 backdrop-blur-md" onClick={onClose} />

                <motion.div
                    className="relative w-full max-w-6xl max-h-[90vh] overflow-hidden bg-[#f5f5f7] rounded-2xl shadow-2xl flex border border-white/50"
                    initial={{ scale: 0.98, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                >
                    {/* SIDEBAR: Facilities (Apple Sidebar Style) */}
                    <div className="w-64 bg-[#f5f5f7] border-r border-[#d2d2d7] flex flex-col shrink-0">
                        <div className="p-5">
                            <div className={`font-bold text-lg ${UI.textMain} flex items-center gap-2`}>
                                Facilities
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-3 space-y-6">
                            {facilityGroups.map(([region, facilities]) => (
                                <div key={region}>
                                    <div className={`text-[10px] font-bold ${UI.textSec} uppercase tracking-wider mb-2 px-3`}>
                                        {region}
                                    </div>
                                    <div className="space-y-0.5">
                                        {facilities.map(([id, bl]) => (
                                            <button
                                                key={id}
                                                onClick={() => handleBeamlineChange(id)}
                                                className={`
                                                    w-full text-left px-3 py-1.5 rounded-lg text-[13px] font-medium transition-all flex items-center justify-between
                                                    ${selectedBeamline === id ? 'bg-white shadow-sm text-[#1d1d1f]' : 'text-[#424245] hover:text-[#1d1d1f] hover:bg-black/5'}
                                                `}
                                            >
                                                <span>{bl.shortName}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* MAIN: Experiments (Clean Grid) */}
                    <div className="flex-1 flex flex-col min-w-0 bg-white/50">
                        <div className="h-16 border-b border-[#e5e5e5] flex items-center justify-between px-6 shrink-0 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
                            <h2 className={`text-lg font-bold ${UI.textMain}`}>Library</h2>
                            <div className="relative w-64">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-9 pr-4 py-1.5 text-sm bg-[#f5f5f7] border-none rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300 transition-all text-[#1d1d1f]"
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 bg-[#f5f5f7]">
                            {/* Filter Tabs */}
                            <div className="flex gap-2 mb-6">
                                <button onClick={() => setViewMode('featured')} className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${viewMode === 'featured' ? 'bg-[#1d1d1f] text-white border-transparent' : 'bg-white border-gray-200 text-[#424245] hover:bg-gray-50'}`}>Featured</button>
                                <button onClick={() => setViewMode('facility')} className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${viewMode === 'facility' ? 'bg-[#1d1d1f] text-white border-transparent' : 'bg-white border-gray-200 text-[#424245] hover:bg-gray-50'}`}>{currentBeamline?.shortName || 'Facility'}</button>
                            </div>

                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                {filteredPresets.map(preset => (
                                    <PresetCard
                                        key={preset.id}
                                        preset={preset}
                                        isActive={state.activePreset === preset.id}
                                        onClick={() => actions.loadPreset(preset)}
                                        featured={preset.featured}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT: CONFIG & STATS */}
                    <div className="w-80 bg-white border-l border-[#d2d2d7] flex flex-col shrink-0">
                        <div className="h-16 border-b border-[#e5e5e5] flex items-center justify-between px-5 shrink-0 bg-white/80 backdrop-blur-sm">
                            <span className={`text-sm font-bold ${UI.textMain}`}>Telemetry</span>
                            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100 text-[#424245] transition-colors">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-5 space-y-8 bg-white">
                            {/* Gauges */}
                            <div className="grid grid-cols-2 gap-4">
                                <CircularGauge value={state.derived.beta} max={1} label="Velocity β" unit="c" decimals={4} />
                                <CircularGauge value={state.derived.gamma} max={20} label="Gamma γ" unit="" decimals={2} />
                            </div>

                            {/* Physics Data */}
                            <div className="space-y-4">
                                <div className={`bg-[#f5f5f7] rounded-xl p-4 border ${UI.border}`}>
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-8 h-8 flex items-center justify-center bg-white rounded border border-gray-200 font-bold text-sm text-[#1d1d1f]">
                                            {state.particle.symbol}
                                        </div>
                                        <div className={`text-sm font-semibold ${UI.textMain}`}>{state.particle.name}</div>
                                    </div>
                                    <div className="space-y-1">
                                        <DataRow label="Mass" value={state.particle.massUnit} />
                                        <DataRow label="Charge" value={`${state.particle.charge > 0 ? '+' : ''}${state.particle.charge}e`} />
                                    </div>
                                </div>

                                <div className={`bg-[#f5f5f7] rounded-xl p-4 border ${UI.border}`}>
                                    <h4 className={`text-[10px] font-bold ${UI.textSec} uppercase tracking-wider mb-2`}>Kinematics</h4>
                                    <DataRow label="Energy" value={state.derived.energy.toFixed(3)} unit="GeV" />
                                    <DataRow label="Decay λ" value={state.derived.decayLength.toFixed(2)} unit="m" />
                                    <DataRow label="MCS θ" value={(state.derived.mcsAngle || 0).toFixed(2)} unit="mrad" />
                                </div>
                            </div>

                            {/* Manual Sliders */}
                            <div>
                                <h4 className={`text-[10px] font-bold ${UI.textSec} uppercase tracking-wider mb-3`}>Beam Control</h4>
                                <div className="space-y-5">
                                    <div>
                                        <div className="flex justify-between mb-1.5">
                                            <span className={`text-xs font-medium ${UI.textSec}`}>Momentum</span>
                                            <span className={`text-xs font-mono font-bold ${UI.textMain}`}>{state.beam.momentum.toFixed(2)} GeV/c</span>
                                        </div>
                                        <input
                                            type="range" min="0.5" max="15" step="0.1"
                                            value={state.beam.momentum}
                                            onChange={(e) => actions.setMomentum(parseFloat(e.target.value))}
                                            className="w-full h-1 bg-gray-200 rounded-full appearance-none cursor-pointer accent-[#1d1d1f]"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-5 border-t border-[#e5e5e5] bg-[#f5f5f7]">
                            <button
                                onClick={() => { 
                                    runSimulation(); // FIX: Trigger the actual engine
                                    onClose(); 
                                }}
                                className="w-full py-3 bg-[#1d1d1f] hover:scale-[1.02] text-white font-medium rounded-xl shadow-lg shadow-gray-200 transition-all flex items-center justify-center gap-2 text-sm"
                            >
                                <Play size={16} fill="currentColor" /> Run Simulation
                            </button>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default ControlRoomModal;
